import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoices } from '../../hooks/useInvoices';
import { Invoice } from '../../mocks/invoices';
import { agencyInfo } from '../../mocks/dashboard';
import InvoiceTable from './components/InvoiceTable';
import InvoiceDetailModal from './components/InvoiceDetailModal';

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { invoices, loading } = useInvoices();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const brand = agencyInfo.brandColor;

  const totalRevenue = invoices.filter((i) => i.status === 'Paid').reduce((s, i) => s + i.totalAmount, 0);
  const totalOutstanding = invoices.filter((i) => i.balanceDue > 0).reduce((s, i) => s + i.balanceDue, 0);
  const overdueCount = invoices.filter((i) => i.status === 'Overdue').length;
  const draftCount = invoices.filter((i) => i.status === 'Draft').length;

  const fmtAmount = (n: number) =>
    `${agencyInfo.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const kpis = [
    { label: 'Total Invoices', value: invoices.length, sub: `${draftCount} drafts`, icon: 'ri-file-list-3-line', color: brand },
    { label: 'Revenue Collected', value: fmtAmount(totalRevenue), sub: 'Paid invoices', icon: 'ri-money-dollar-circle-line', color: '#10B981' },
    { label: 'Outstanding', value: fmtAmount(totalOutstanding), sub: `${invoices.filter((i) => i.balanceDue > 0).length} invoices`, icon: 'ri-time-line', color: '#F59E0B' },
    { label: 'Overdue', value: overdueCount, sub: 'Require attention', icon: 'ri-alarm-warning-line', color: '#EF4444' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create, send, and track customer invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-download-2-line" />
            Export
          </button>
          <button
            onClick={() => navigate('/invoices/new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-add-line" />
            New Invoice
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-5 mb-8">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${kpi.color}15` }}>
                <i className={`${kpi.icon} text-lg`} style={{ color: kpi.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            <p className="text-sm font-medium text-gray-700 mt-1">{kpi.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Invoice Table */}
      <InvoiceTable invoices={invoices} onSelect={(inv) => setSelectedInvoice(inv)} />

      {/* Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
      )}
    </div>
  );
}
